import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MIXED_SPORTS, mixedProtected, mixedScore, readMixedStorage, shiftMixedDate, stockholmToday } from './mixedMatchData';
import useMixedMatches from './useMixedMatches';
import { isLiveGame, useFollowSettingsVersion } from '../followLists';
import { useFollowVersion } from '../FollowControls';
import './MatchList.css';
import './MinaPage.css';

// "Mina matcher och lag": everything the user follows, across all sports.
// The only list with interactive stars. A removed row stays until the Ångra toast has ended.

const PLAYED_DAYS = 14, UPCOMING_DAYS = 7, PLAYED_STEP = 5;
const SPORT_BY_ID = Object.fromEntries(MIXED_SPORTS.map((sport) => [sport.id, sport]));

const startOf = (game) => game.starts_at || game.match_time || (game.date && game.time ? `${game.date}T${game.time}` : game.date || '');
const dateOf = (game) => startOf(game).slice(0, 10);
const timeOf = (game) => game.time || (/T\d{2}:\d{2}/.test(startOf(game)) ? startOf(game).slice(11, 16) : '');
const minutes = (value) => { const part = /^(\d{1,2}):(\d{2})$/.exec(value || ''); return part ? +part[1] * 60 + +part[2] : Infinity; };

// Pure: build the rows of the Mina page from fetched games, saved snapshots and followed teams.
export function buildMinaRows({ games, followedGames, teamIds }) {
  const follows = window.SportsHQFollows;
  const byKey = new Map(followedGames.map((row) => [row.k, row]));
  const rows = [], seen = new Set();
  for (const game of games) {
    const sport = game.sport.id;
    const key = follows.keyFromLink(game.match_link, sport);
    const rowId = `${sport}:${game.id}`;
    if (seen.has(rowId)) continue;
    let reason = null;
    if (key && byKey.has(key)) reason = { kind: 'game', key, sport, label: 'Match' };
    else {
      const side = [['home_team_id', 'home_team'], ['away_team_id', 'away_team']].find(([id]) => teamIds[sport]?.includes(game[id]));
      if (side) reason = { kind: 'team', sport, id: game[side[0]], label: game[side[1]] };
    }
    if (!reason) continue;
    seen.add(rowId);
    rows.push({ id: `g:${key || rowId}`, sport, reason, game, date: dateOf(game), time: timeOf(game), live: isLiveGame(game),
      league: game.league || game.competition_name || '', home: game.home_team, away: game.away_team, logos: [game.home_logo, game.away_logo],
      link: game.match_link || '', snapshot: false, key });
    if (key) byKey.delete(key);
  }
  // Followed games that were not in the fetched lists: show the saved snapshot.
  for (const row of byKey.values()) {
    const sport = row.k.split(':')[1], snap = row.s || {};
    rows.push({ id: `g:${row.k}`, sport, reason: { kind: 'game', key: row.k, sport, label: 'Match' }, game: null,
      date: (snap.st || '').slice(0, 10), time: /T\d{2}:\d{2}/.test(snap.st || '') ? snap.st.slice(11, 16) : '', live: false,
      league: snap.lg || '', home: snap.h || '', away: snap.a || '', logos: [], link: snap.l || '', snapshot: true, key: row.k });
  }
  return rows;
}

// Pure: place rows in Pågår nu / Idag / Kommande / Spelade.
export function groupMinaRows(rows, today) {
  const later = shiftMixedDate(today, UPCOMING_DAYS), earliest = shiftMixedDate(today, -PLAYED_DAYS);
  const order = (a, b) => (a.date || '9999').localeCompare(b.date || '9999') || (minutes(a.time) - minutes(b.time) || 0);
  const groups = { live: [], today: [], upcoming: [], played: [] };
  for (const row of rows) {
    if (row.live) groups.live.push(row);
    else if (row.date === today) groups.today.push(row);
    else if (row.date > today || !row.date) {
      // Teams: next days only. Explicitly followed games: whenever they are.
      if (row.reason.kind === 'game' || row.date <= later) groups.upcoming.push(row);
    } else if (row.reason.kind === 'game' && row.date >= earliest) groups.played.push(row);
  }
  groups.live.sort(order); groups.today.sort(order); groups.upcoming.sort(order);
  groups.played.sort((a, b) => order(b, a));
  return groups;
}

function Star({ reason, home, away, link, start, league }) {
  if (reason.kind === 'game') {
    return <shq-follow type="game" sport={reason.sport} game-key={reason.key} home={home} away={away} link={link} start={start} league={league} />;
  }
  return <shq-follow type="team" sport={reason.sport} team-id={String(reason.id)} team-name={reason.label} />;
}

function Row({ row, date, hidden }) {
  const protectedScore = row.game ? mixedProtected(row.game, hidden) : false;
  let score = row.game ? mixedScore(row.game, date, protectedScore) : null;
  if (!score) score = row.date && row.date < date ? { text: '–', label: 'Resultat saknas' } : { text: row.time || 'Tid ej klar', label: '' };
  const href = row.link ? `/${row.sport}/match?${new URLSearchParams({ url: row.link + '/laguppstallning', returnTo: '/matches/?vy=mina' })}${protectedScore ? '&push_protected=1' : ''}` : null;
  const when = row.date && row.date !== date ? new Intl.DateTimeFormat('sv-SE', { weekday: 'short', day: 'numeric', month: 'numeric' }).format(new Date(row.date + 'T12:00:00')).replace('.', '') : '';
  const body = <>
    <div className="league-info"><span className="mixed-sport-label">{SPORT_BY_ID[row.sport]?.label}</span><span aria-hidden="true">·</span>{row.league}</div>
    <div className="mina-row__reason"><span aria-hidden="true">★</span> {row.reason.label}</div>
    <div className="match-details">
      <div className="team">{row.logos[0] && <img className="team-logo" src={row.logos[0]} alt="" loading="lazy" />}<span className="team-name">{row.home}</span></div>
      <div className="match-time"><span className={protectedScore ? 'hidden-result' : ''}>{score.text}</span>
        {when && <span className="mina-row__when">{when}</span>}
        {score.label && <span className={`match-status match-status--${row.game?.status?.status_id || 0}`}>{score.label}</span>}</div>
      <div className="team"><span className="team-name">{row.away}</span>{row.logos[1] && <img className="team-logo" src={row.logos[1]} alt="" loading="lazy" />}</div>
    </div>
  </>;
  return <div className={`match-card mina-row${row.live ? ' match-card--followed-live' : ''}${row.ghost ? ' mina-row--ghost' : ''}`} data-row={row.id}>
    {href ? <a className="mina-row__link" href={href}>{body}</a> : <div className="mina-row__link">{body}</div>}
    {row.live && <span className="match-card__marker"><span className="match-card__live"><span className="match-card__live-dot" aria-hidden="true" />Live</span></span>}
    <span className="mina-row__star"><Star reason={row.reason} home={row.home} away={row.away} link={row.link} start={row.game ? startOf(row.game) : `${row.date}${row.time ? `T${row.time}` : ""}`} league={row.league} /></span>
  </div>;
}

export default function MinaPage() {
  const [params, setParams] = useSearchParams();
  const today = stockholmToday();
  const tab = params.get('tab') === 'lag' ? 'lag' : 'matcher';
  const { games: todayGames, loading, failures } = useMixedMatches(today);
  const [upcoming, setUpcoming] = useState([]);
  const [playedShown, setPlayedShown] = useState(PLAYED_STEP);
  const [lingering, setLingering] = useState({});
  const version = useFollowVersion() + useFollowSettingsVersion();
  const follows = window.SportsHQFollows;
  const hidden = Boolean(readMixedStorage('sports-hq-mixed-hide-results', false));

  useEffect(() => {
    let active = true; const controller = new AbortController();
    Promise.allSettled(MIXED_SPORTS.map(async (sport) => {
      const response = await fetch(`${sport.api}/upcoming`, { signal: controller.signal });
      if (!response.ok) throw Error();
      const body = await response.json();
      return (body.matches || []).map((game) => ({ ...game, sport }));
    })).then((results) => { if (active) setUpcoming(results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []))); });
    return () => { active = false; controller.abort(); };
  }, []);

  const followedGames = follows.followedGames();
  const teamIds = Object.fromEntries(MIXED_SPORTS.map((sport) => [sport.id, follows.followedIds(sport.id)]));
  const allGames = useMemo(() => [...todayGames.map((game) => (dateOf(game) ? game : { ...game, date: today })), ...upcoming], [todayGames, upcoming, today]);
  const fresh = buildMinaRows({ games: allGames, followedGames, teamIds });
  const teamName = (sport, id) => allGames.reduce((found, game) => found || (game.sport.id === sport && game.home_team_id === id ? game.home_team : game.sport.id === sport && game.away_team_id === id ? game.away_team : ''), '')
    || follows.teamNames()[`${sport}:${id}`]?.n || (sport === 'floorball' ? `${'Lag'} ${id}` : String(id));
  const teamRows = MIXED_SPORTS.flatMap((sport) => teamIds[sport.id].map((id) => {
    const next = allGames.filter((game) => game.sport.id === sport.id && (game.home_team_id === id || game.away_team_id === id) && dateOf(game) >= today)
      .sort((a, b) => (dateOf(a) + (timeOf(a) || '99:99')).localeCompare(dateOf(b) + (timeOf(b) || '99:99')))[0];
    return { id: `t:${sport.id}:${id}`, sport: sport.id, teamId: id, name: teamName(sport.id, id), next };
  }));

  // Rows that just disappeared (unfollow) stay for the undo window, then collapse.
  const previous = useRef(new Map());
  const owner = window.SportsHQSettings?.accountId || null;
  const previousOwner = useRef(owner);
  const currentIds = new Map([...fresh.map((row) => [row.id, row]), ...teamRows.map((row) => [row.id, row])]);
  useEffect(() => {
    const clear = () => setLingering({});
    window.addEventListener('sports-hq-follow-toast-dismiss', clear);
    return () => window.removeEventListener('sports-hq-follow-toast-dismiss', clear);
  }, []);
  useEffect(() => {
    if (previousOwner.current !== owner) {
      previousOwner.current = owner;
      previous.current = currentIds;
      setLingering({});
      return;
    }
    const undoTarget = follows.undoTarget;
    const dropped = [...previous.current].filter(([id, row]) => !currentIds.has(id) && undoTarget &&
      (id === undoTarget || row.reason?.kind === 'team' && `t:${row.sport}:${row.reason.id}` === undoTarget));
    previous.current = currentIds;
    if (!dropped.length) return;
    setLingering((old) => ({ ...old, ...Object.fromEntries(dropped.map(([id, row]) => [id, row])) }));
    // The shared toast owns the undo lifetime, including hover/focus pauses.
    // Its dismissal (expiry, Escape, replacement or undo) clears these rows.
  }, [version, todayGames, upcoming, owner]);

  const ghosts = previousOwner.current === owner && follows.undoTarget
    ? Object.values(lingering).filter((row) => !currentIds.has(row.id)) : [];
  const gameRows = [...fresh, ...ghosts.filter((row) => row.id.startsWith('g:')).map((row) => ({ ...row, ghost: true }))];
  const groups = groupMinaRows(gameRows, today);
  const teamsShown = [...teamRows, ...ghosts.filter((row) => row.id.startsWith('t:'))];
  const anything = followedGames.length > 0 || teamRows.length > 0 || ghosts.length > 0;
  const guest = window.SportsHQSettings && window.SportsHQSettings.sessionKnown && !window.SportsHQSettings.authenticated;
  const setTab = (next) => { const query = new URLSearchParams(params); if (next === 'lag') query.set('tab', 'lag'); else query.delete('tab'); setParams(query, { replace: true }); };
  const loginHref = '/profile/?' + new URLSearchParams({ login: '1', return: '/matches/?vy=mina' });
  const sections = [['live', 'Pågår nu'], ['today', 'Idag'], ['upcoming', 'Kommande']];
  const played = groups.played;

  return <main className="match-list-container mixed-matches mina-page">
    <div className="mina-page__top"><a className="mina-page__back" data-shq-return href={window.SportsHQNavigation?.rootTarget("/matches/") || "/matches/"}><span aria-hidden="true">←</span> {'Alla matcher'}</a></div>
    <h1 className="mina-page__title">{'Mina matcher och lag'}</h1>
    <div className="mina-tabs" role="group" aria-label={'Visa'}>
      <button type="button" aria-pressed={tab === 'matcher'} onClick={() => setTab('matcher')}>{'Matcher'}</button>
      <button type="button" aria-pressed={tab === 'lag'} onClick={() => setTab('lag')}>{'Lag'}</button>
    </div>
    <div className="mixed-status" role="status" aria-live="polite">
      {loading && anything ? 'Hämtar matcher…' : null}
      {failures.length > 0 && <p>{'Kunde inte hämta'} {failures.join(', ')}. {'Visar sparad information.'}</p>}
    </div>
    {!anything && <div className="mina-empty">
      <p className="mina-empty__icon" aria-hidden="true">☆</p>
      <p className="mina-empty__title">{'Du följer inget än.'}</p>
      <p>{'Tryck på stjärnan vid ett lag eller en match.'}</p>
      <a className="mina-empty__cta" href="/matches/">{'Visa dagens matcher'}</a>
    </div>}
    {anything && tab === 'matcher' && <div className="match-list match-list--compact">
      {!groups.live.length && !groups.today.length && !groups.upcoming.length && !played.length && !loading && <p className="mina-note">{'Dina lag har inga matcher de närmaste dagarna.'}</p>}
      {sections.map(([id, title]) => groups[id].length > 0 && <section key={id} className="mina-section" aria-label={title}>
        <h2 className="follow-section__title">{title}</h2>
        {groups[id].map((row) => <Row key={row.id} row={row} date={today} hidden={hidden} />)}
      </section>)}
      {played.length > 0 && <section className="mina-section" aria-label={'Spelade'}>
        <h2 className="follow-section__title">{'Spelade'}</h2>
        {played.slice(0, playedShown).map((row) => <Row key={row.id} row={row} date={today} hidden={hidden} />)}
        {played.length > playedShown && <button type="button" className="mina-more" onClick={() => setPlayedShown(playedShown + PLAYED_STEP)}>{'Visa fler'}</button>}
      </section>}
    </div>}
    {anything && tab === 'lag' && <div className="match-list match-list--compact">
      {!teamsShown.length && <p className="mina-note">{'Du följer inga lag än.'}</p>}
      {MIXED_SPORTS.map((sport) => {
        const list = teamsShown.filter((row) => row.sport === sport.id);
        return list.length > 0 && <section key={sport.id} className="mina-section" aria-label={sport.label}>
          <h2 className="follow-section__title">{sport.label}</h2>
          {list.map((row) => {
            const next = row.next, opponent = next && (next.home_team_id === row.teamId ? next.away_team : next.home_team);
            const when = next && new Intl.DateTimeFormat('sv-SE', { weekday: 'short' }).format(new Date(dateOf(next) + 'T12:00:00')).replace('.', '');
            return <div key={row.id} className={`mina-team${lingering[row.id] && !currentIds.has(row.id) ? ' mina-row--ghost' : ''}`}>
              <div className="mina-team__text"><strong>{row.name}</strong>
                <small>{next ? `${dateOf(next) === today ? 'Idag' : when} ${timeOf(next)} ${'mot'} ${opponent}` : 'Ingen kommande match hittad'}</small></div>
              <span className="mina-row__star"><shq-follow type="team" sport={row.sport} team-id={String(row.teamId)} team-name={row.name} /></span>
            </div>;
          })}
        </section>;
      })}
    </div>}
    {guest && anything && <p className="mina-footer">{'Sparat på den här enheten. Logga in för att ha det på alla dina enheter.'} <a href={loginHref}>{'Logga in'}</a></p>}
  </main>;
}
